import { c as _c } from "react/compiler-runtime";
import SceneLogo from "@assets/logo/SCENE_logo.svg?react";
import SphereXgLogo from "@assets/logo/sphere-xg-logo.svg?react";
import { HELD_SEAT_REASON, isSeatLocked, isSeatSoldOut } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatUsageText } from "@components/table/members/assign-seats/seat-usage-text";
import { Checkbox, ExclamationMarkCircleFillIcon, InfoCircleFillIcon, Text } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { MEMBER_SEAT_DESCRIPTIONS, MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
/** The logo of each seat, as the members table shows it */
const SEAT_LOGOS = {
  xgSeat: SphereXgLogo,
  sceneSeat: SceneLogo
};

/** The size of the seat logos, which matches the logos in the members table */
const LOGO_SIZE = "24px";

/** The opacity of the logo of a seat the member can neither get nor holds */
const FADED_LOGO_OPACITY = 0.5;

/** The size of the icons in front of the status of a seat, matching the line of text they lead */
const STATUS_ICON_SX = {
  width: "1rem",
  height: "1rem",
  flexShrink: 0
};
/**
 * A status line of a seat, e.g. why it cannot be changed. It leads with an icon and is set in the
 * primary text color, so that it does not read as part of the description above it.
 */
function SeatStatus(t0) {
  const $ = _c(9);
  const {
    icon,
    color,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 0.5,
      alignItems: "center",
      mt: 0.5
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = color ?? "text.primary";
  let t3;
  if ($[1] !== t2) {
    t3 = {
      color: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <Text variant="bodyS" component="span" sx={t3}>{children}</Text>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== icon || $[7] !== t4) {
    t5 = <Stack direction="row" component="span" sx={t1}>{icon}{t4}</Stack>;
    $[6] = icon;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
/** Shows why a seat cannot be given to the member, or taken away from them */
function SeatCheckboxStatus(t0) {
  const $ = _c(9);
  const {
    option
  } = t0;
  if (!option.blockReason || option.blockReason === HELD_SEAT_REASON || isSeatSoldOut(option)) {
    return null;
  }
  const isError = option.blockSeverity === "error";
  const Icon = isError ? ExclamationMarkCircleFillIcon : InfoCircleFillIcon;
  const t1 = isError ? "error.main" : "text.secondary";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      ...STATUS_ICON_SX,
      color: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Icon || $[3] !== t2) {
    t3 = <Icon sx={t2} />;
    $[2] = Icon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isError ? "error.main" : undefined;
  let t5;
  if ($[5] !== option.blockReason || $[6] !== t3 || $[7] !== t4) {
    t5 = <SeatStatus icon={t3} color={t4}>{option.blockReason}</SeatStatus>;
    $[5] = option.blockReason;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

/**
 * A seat to select, shown as a card with its logo and what it lets the member do, and in its corner
 * whether the member holds it or what changing it does to the seats of the workspace. A checked
 * seat is outlined in the primary color. A seat whose state cannot be changed is shaded, and tells
 * why unless the member simply holds it already.
 */
export function SeatCheckbox(t0) {
  const $ = _c(46);
  const {
    option,
    isChecked,
    seatsAvailable,
    seatsUsed,
    isDisabled: t1,
    onChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const Logo = SEAT_LOGOS[option.seat];
  let t2;
  if ($[0] !== option) {
    t2 = isSeatLocked(option);
    $[0] = option;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isLocked = t2;
  const t3 = isDisabled || isLocked;
  let t4;
  if ($[2] !== isChecked || $[3] !== onChange || $[4] !== option.seat) {
    t4 = () => onChange(option.seat, !isChecked);
    $[2] = isChecked;
    $[3] = onChange;
    $[4] = option.seat;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isChecked ? "primary.main" : "divider";
  const t6 = isLocked ? "action.hover" : "transparent";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = {
      alignItems: "flex-start",
      m: 0,
      p: 1.5,
      border: 1,
      borderRadius: 1,
      borderColor: t5,
      backgroundColor: t6
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      gap: 1.5,
      pt: 0.25,
      width: "100%"
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = isLocked && !option.isHeld ? FADED_LOGO_OPACITY : 1;
  let t10;
  if ($[10] !== t9) {
    t10 = {
      display: "block",
      flexShrink: 0,
      opacity: t9
    };
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== Logo || $[13] !== t10) {
    t11 = <Logo height={LOGO_SIZE} width={LOGO_SIZE} aria-hidden={true} style={t10} />;
    $[12] = Logo;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      flex: 1,
      minWidth: 0
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      gap: 1,
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      lineHeight: LOGO_SIZE
    };
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = MEMBER_SEAT_LABELS[option.seat];
  let t16;
  if ($[18] !== t15) {
    t16 = <Text variant="heading14" component="span" sx={t14}>{t15} seat</Text>;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== isChecked || $[21] !== option || $[22] !== seatsAvailable || $[23] !== seatsUsed) {
    t17 = <SeatUsageText option={option} isChecked={isChecked} seatsAvailable={seatsAvailable} seatsUsed={seatsUsed} />;
    $[20] = isChecked;
    $[21] = option;
    $[22] = seatsAvailable;
    $[23] = seatsUsed;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16 || $[26] !== t17) {
    t18 = <Stack direction="row" component="span" sx={t13}>{t16}{t17}</Stack>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      color: "text.secondary"
    };
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = MEMBER_SEAT_DESCRIPTIONS[option.seat];
  let t21;
  if ($[29] !== t20) {
    t21 = <Text variant="bodyS" component="span" sx={t19}>{t20}</Text>;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== option) {
    t22 = <SeatCheckboxStatus option={option} />;
    $[31] = option;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t18 || $[34] !== t21 || $[35] !== t22) {
    t23 = <Stack component="span" sx={t12}>{t18}{t21}{t22}</Stack>;
    $[33] = t18;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t11 || $[38] !== t23) {
    t24 = <Stack direction="row" component="span" sx={t8}>{t11}{t23}</Stack>;
    $[37] = t11;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== isChecked || $[41] !== t24 || $[42] !== t3 || $[43] !== t4 || $[44] !== t7) {
    t25 = <Checkbox checked={isChecked} disabled={t3} onChange={t4} fullWidth={true} controlSx={t7} label={t24} />;
    $[40] = isChecked;
    $[41] = t24;
    $[42] = t3;
    $[43] = t4;
    $[44] = t7;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  return t25;
}